import isValid from 'date-fns/isValid';
import { toZonedTime, format } from 'date-fns-tz';
import { getTextWidth } from 'get-text-width';
import i18next from 'i18next';
import { INTERNAL_EXTERNAL } from '@/components/UI/FilterSelection/FilterSelection';
import TIMEZONES from '@/components/UserSettingsMenu/UserSettingsMenu.utils';
import { proccessCampaignTrans } from '@/containers/CampaignDetail/Translations/translations-utils';
import authUtils from './authUtils';
import {
  helpTextTabs, defaultLanguage, goToSummaryStatuses, statusCampaign, summaryTabs, subCRSummaryTabs, CART_ROLE_PREFIX,
  LANGUAGES, GERMAN, collapsedLabelsWorkflow, DAY_DATE_FORMAT, LANGUAGES_ABBREVIATION, statusSubCampaign, WORKFLOW_TYPE,
  FIELDS_DISABLED_BY_PDRS, PDRS, INTERNAL, EXTERNAL, YES, BASE_PATH, CR_TYPE,
} from './constants';

export const formatDate = (originalDate) => {
  if (!originalDate || originalDate === 'null' || !isValid(originalDate)) {
    return '';
  }

  const date = new Date(originalDate);
  const day = (`0${date.getDate()}`).slice(-2);
  const month = (`0${date.getMonth() + 1}`).slice(-2);
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

export const getNativeDateValue = (value) => {
  if (!value) return '';

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = (`0${date.getMonth() + 1}`).slice(-2);
  const day = (`0${date.getDate()}`).slice(-2);

  return `${year}-${month}-${day}`;
};

export const convertLocaleToUTC = (date) => new Date(
  date.getUTCFullYear(),
  date.getUTCMonth(),
  date.getUTCDate(),
  date.getUTCHours(),
  date.getUTCMinutes(),
  date.getUTCSeconds(),
  date.getUTCMilliseconds(),
);

export const changeTimeZoneFormat = (timeZone) => (timeZone ? TIMEZONES.find(({ code }) => code === timeZone)?.format : '');

const processStringDate = (stringDate) => (// 2022-07-12 08:31:07:30667447000000 +0000 to 2022-07-12 08:31:07 +0000
  !Number.isNaN((new Date(stringDate)).getTime()) ? stringDate : stringDate?.replace(/:\d{3}\d*\b/g, '')
);

export const getFullFormattedDate = (originalDate, timeZone, myFormat) => {
  if (!originalDate || originalDate === 'null' || originalDate === '-') {
    return '-';
  }

  const processedDate = processStringDate(originalDate);
  const dateObject = new Date(processedDate);

  if (Number.isNaN(dateObject.getTime())) {
    return '-';
  }

  const zoneTime = toZonedTime(dateObject, changeTimeZoneFormat(timeZone));

  return format(zoneTime, myFormat);
};

export const stringToDate = (date) => {
  const fullFormatDateRegex = new RegExp(/(\d{4})-(\d{2})-(\d{2})\s(\d{2}):(\d{2}):(\d{2})\s(\W{1})(\d{4})/);

  if (!date) {
    return null;
  }

  const [year, month, day] = date?.split('-');

  if (fullFormatDateRegex.test(date)) return new Date(year, +month - 1, day.slice(0, 2));

  return new Date(year, +month - 1, day);
};

export const getAppLanguage = () => {
  const LanguagesApp = ['en', 'de'];
  const lang = LanguagesApp.includes(navigator.language.slice(0, 2)) ? navigator.language.slice(0, 2) : defaultLanguage;

  return lang.toLowerCase();
};

export const isValueBiggerThanColumnWidth = (props, tableColumnExtensions) => {
  const columnExtensions = tableColumnExtensions?.find(({ columnName }) => columnName === props?.column?.name);
  const propsColumnWidth = columnExtensions?.width - columnExtensions?.extraWidthTooptip ?? 0;

  return getTextWidth(props.value) >= propsColumnWidth;
};

export const getTabLabelByLink = (link) => {
  const tab = helpTextTabs.find((e) => e.link === link);

  return tab?.label || '';
};

export const debounce = (fn, time = 500) => {
  let canClick = true;

  return (...args) => {
    if (canClick) {
      canClick = false;
      fn.apply(this, args);

      setTimeout(() => {
        canClick = true;
      }, time);
    }
  };
};

export const getVersionUrlParam = (version) => ((version && `/${version}`) || '');

export const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

export const getErrorCode = (err) => err?.code || err?.errorCode || err?.response?.data?.errorCode;

export const formatStringDateToString = (newsDate) => {
  const newsYear = newsDate.slice(0, 4);
  const newsMonth = newsDate.slice(5, 7);
  const newsDay = newsDate.slice(8, 10);
  const publicationDate = (`${newsDay}.${newsMonth}.${newsYear}`);

  return publicationDate;
};

export const checkDate = ({ date, label, handleChange }) => {
  if (date === null) {
    handleChange(label, null);

    return;
  }

  handleChange(label, formatDate(date));
};

export const modulesToList = (translations, campaign) => (
  {
    CAMPAIGN: proccessCampaignTrans(translations?.modules?.CAMPAIGN, campaign),
    DESCRIPTION: translations?.modules?.DESCRIPTION,
    CLASSIFICATION: translations?.modules?.CLASSIFICATION,
    AFFECTED_HARDWARE: translations?.modules?.AFFECTED_HARDWARE,
    AFFECTED_SOFTWARE: translations?.modules?.AFFECTED_SOFTWARE,
    AFFECTED_VEHICLES: translations?.modules?.AFFECTED_VEHICLES,
  });

export const mapUpdateTranslationsData = (campaign) => ({
  ...campaign.translations,
  modules: modulesToList(campaign.translations, campaign),
});

export const getInitialCampaignState = () => ({
  originalValues: {
    hasChange: false,
    changesIn: {},
    values: {},
  },
  help: {
    show: true,
    focus: null,
    invalidLanguages: [],
  },
  history: {
    show: false,
    statuses: null,
  },
  info: {},
  description: {},
  attachments: {},
  classification: {},
  affectedHardware: {},
  affectedSoftware: {},
  affectedVehicles: {
    typeOfAffectedPlantsSelection: 'SERIES',
    technicalAssessment: {},
    volumes: [],
    series: [],
    affectedPlants: [],
    lastMSAdded: false,
  },
  limitationPlants: {},
  copyVehicleModelDesignation: {},
  copyAggregate: {},
  recipients: {},
  translations: {},
  summary: {},
  plantFeedback: {},
  validations: {
    active: {
      all: false,
      min: {},
    },
    classification: {},
    recipients: {},
    affectedVehicles: {},
    translations: {},
  },
  changesFromVersion: {
    versionToCompare: null,
    active: false,
    description: {},
    classification: {},
    recipients: {},
    affectedVehicles: {},
    affectedSoftware: {},
    affectedHardware: {},
  },
  campaignSearch: {
    resultCounter: 0,
    sections: {
      classification: {},
      affectedHardware: {},
      affectedSoftware: {},
      affectedVehicles: {},
      affectedModelSeries: {},
      limitationVolumes: {},
      recipients: {},
      campaign: {},
      description: {},
    },
    searchedText: '',
    active: false,
    selectedCoincidenceFieldId: '',
    selectedCoincidenceTab: '',
    highlightTab: '',
  },
});

export const removeSpacesFromString = (data) => data?.replace(/\s/g, '');

export const tabLowerCase = (tab) => tab?.toLowerCase();

export const getCRidBySubCRid = (id) => {
  // Delete plant from SubCR id: CR-QM_SEC_B-2022-003695-0175-0001 -> CR-QM_SEC_B-2022-003695  (-175)
  const idCR = id.split('-');
  idCR.pop();
  idCR.pop();

  return idCR.join('-');
};

// return penultimate position
export const getPlantIdBySubCRId = (id = '') => {
  const subCRId = id.split('-');
  subCRId.pop();

  return subCRId.pop();
};

export const isSummary = () => (
  [...summaryTabs, ...subCRSummaryTabs].map((tab) => `/${tab.link}`).some(
    (tab) => window.location.pathname.includes(tab),
  ));

export const isSubCR = () => window.location.pathname.includes('/sub-campaign/');

export const isSubCRById = (campaignId) => campaignId.split('-').length === 6;

export const getCampaignId = (id) => (isSubCR() ? getCRidBySubCRid(id) : id);

export const isObject = (item) => (typeof item === 'object' && !Array.isArray(item) && item !== null);

export const urlToTabKey = (urlToTabs) => urlToTabs?.[tabLowerCase(window.location.pathname.split('/')[4])];

// Compare 2 objects and exclude the property you want (or none)
export const equalObjs = (object1, object2, exclude) => {
  const { [exclude]: out1, ...obj1 } = object1;
  const { [exclude]: out2, ...obj2 } = object2;
  if (Object.keys(obj1).toString() !== Object.keys(obj2).toString()) return false;

  let areEqual = true;

  Object.entries(obj1).forEach(([key, value]) => {
    if (typeof value === 'object' && !Array.isArray(value)) {
      areEqual = areEqual && equalObjs(value, obj2[key]);
    } else {
      areEqual = areEqual && JSON.stringify(value) === JSON.stringify(obj2[key]);
    }
  });

  return areEqual;
};

export const createRowLink = (row, subCR, freeTextFilter) => {
  const id = row.mainCR || row.subCR || row.hiddenSubCR;
  const baseUrl = row.isHistory ? `${BASE_PATH}/campaign/${id}/${row.version}` : `${BASE_PATH}/campaign/${id}`;
  const isSubCr = row?.isSubCRChildren || subCR; // subcr children of CR or Subcr overview

  if (row?.thirdLevelRow || row?.fourthLevelRow) {
    return `${BASE_PATH}/sub-campaign/${id}${row.isHistory ? `/${row.version}` : ''}/feedback?campaign=${row.campaign || row.hiddenCampaign}${(freeTextFilter && freeTextFilter.value !== '') ? `&search=${freeTextFilter.value}` : ''}`;
  }

  if (!isSubCr && freeTextFilter && freeTextFilter.value !== '') {
    return `${baseUrl}/description?search=${freeTextFilter.value}`;
  }

  if (isSubCr) return `${BASE_PATH}/sub-campaign/${id}${row.isHistory ? `/${row.version}` : ''}/feedback${(freeTextFilter && freeTextFilter.value !== '') ? `?search=${freeTextFilter.value}` : ''}`;

  if (goToSummaryStatuses.includes(row.statusCode)) return `${baseUrl}/summary`;
  if (row.statusCode === statusCampaign.REQUESTED_FOR_CHANGES) return `${BASE_PATH}/campaign/${id}/${row.version}/summary`;

  return `${baseUrl}/description`;
};

export const arrayMove = (arr, oldIndex, newIndex) => {
  if (oldIndex === -1) return arr;

  const from = arr.splice(oldIndex, 1)[0];
  arr.splice(newIndex, 0, from);

  return arr;
};

export const haveFilterOrActiveSorting = (filter, sorting, defaultSorting) => {
  if (sorting && sorting.length > 0 && defaultSorting && defaultSorting.length > 0) {
    if (sorting[0]?.columnName === defaultSorting[0]?.columnName && sorting[0]?.direction === defaultSorting[0]?.direction) {
      if ((!filter || filter?.length === 0) || (filter && filter?.length > 0 && filter.every((f) => f.value === ''))) {
        return false;
      }
    }
  }

  return true;
};

export const isCampaignInRD = (status) => status === statusCampaign.IN_RD_COMMITTEE;

export const isMPDepartment = (department) => department?.startsWith('MP');

export const isCampaignRDMP = (department) => department?.startsWith('RD') || isMPDepartment(department);

export const isUserInRecipients = (recipients, userCode) => Object.keys(recipients || {}).includes(userCode);

export const toLanguageObject = (objectKey, translationKey, languages = LANGUAGES_ABBREVIATION, extraLabel = '') => {
  const isCRStatus = Object.entries(statusCampaign).some(([key]) => key === objectKey);
  const isVANSStatus = false;

  return {
    [objectKey]: languages.reduce((acc, lang) => {
      acc[lang] = `${i18next.t(translationKey, { lng: lang.toLowerCase() })}${extraLabel ? ` ${extraLabel}` : ''}`;
      acc['type'] = [`${isCRStatus ? 'CR' : 'SUBCR'}`, ...isVANSStatus ? ['VANS'] : []];
      acc['key'] = objectKey;

      return acc;
    }, {}),
  };
};

export const formatFullRole = (role) => role.replace(CART_ROLE_PREFIX, '').replaceAll('_', ' ');

export const backFormatRoleToCleanFormat = (role) => formatFullRole(authUtils.backRoleToOauthRole(authUtils.stripCartPrefix(role)));

export const getTitleWithoutDoormanText = (title, lang, isDoorman) => (isDoorman
  ? title.replaceAll(`${i18next.t('campaignRequests.doormanFixedTitle', { lng: lang.toLowerCase() })}: `, '')
  : title);

export const putDoormanTextTitle = (title, lang, isDoorman) => (
  (isDoorman && !title.startsWith(`${i18next.t('campaignRequests.doormanFixedTitle', { lng: lang.toLowerCase() })}: `))
    ? `${i18next.t('campaignRequests.doormanFixedTitle', { lng: lang.toLowerCase() })}: ${title}`
    : title);

export const getBrowserLanguageAsDefault = () => {
  const langFound = LANGUAGES.find((lang) => navigator.language.startsWith(lang.code));

  return langFound ? langFound.code : GERMAN.toLowerCase();
};

export const getBrowserUtcAsDefault = () => {
  const utc = new Date().toString().match(/([-\\+][0-9]+)\s/)[1];
  const sign = utc.substr(0, 1);
  const firstPart = utc.substr(1, 1) === '0' ? utc.substr(2, 1) : utc.substr(1, 2);
  const lastPart = utc.substr(3, 1) === '0' ? '' : `:${utc.substr(3, 2)}`;

  return `UTC ${sign}${firstPart}${lastPart}`;
};

function isDescendant(parent, child) {
  let node = child?.parentElement;

  while (node != null) {
    if (node === parent) {
      return true;
    }
    node = node.parentElement;
  }

  return false;
}

export const scrollToField = (parentId, fieldId, isContainerParent) => {
  const tableContainers = document.querySelectorAll('[class^="MuiTable-root"]');
  let container = document.getElementById(parentId);
  container = isContainerParent ? container.parentElement : container;
  const field = document.getElementById(fieldId);

  if (!container || !field) {
    return;
  }
  const currentScrollTop = container.scrollTop;
  const fieldTop = field?.getBoundingClientRect().top;
  const top = currentScrollTop + fieldTop - 300; // loop over tableContainer to check if the field belongs to the table, scroll the table

  for (let i = 0; i < tableContainers?.length; i += 1) {
    const table = tableContainers[i].parentElement.parentElement;

    if (isDescendant(table, field)) {
      const tableWidth = table.offsetWidth;
      const cellWidth = field?.offsetWidth;
      const currentScrollLeft = table.scrollLeft;
      const cellLeft = field?.getBoundingClientRect().left;
      const left = (currentScrollLeft + cellLeft) - (tableWidth / 2) - cellWidth;
      table.scrollTo(left, 0);
      break;
    }
  }
  container?.scrollTo(0, top);
};

export const isValidEmail = (email) => email && /^[_a-zA-Z0-9-]+(\.[_a-zA-Z0-9-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,4})$/.test(email);

export const reverseObjectKeysAndValues = (obj) => Object.fromEntries(Object.entries((obj)).map(([key, value]) => [value, key]));

export const getAvailableComment = (commentObj, preferredLanguage) => commentObj?.[preferredLanguage] || commentObj?.[preferredLanguage === 'EN' ? 'DE' : 'EN'];

export const formatSummaryUser = (user) => `${user?.lastName.toUpperCase()}, ${user?.firstName.toUpperCase()}, ${user?.department}`;

export const getArrayWithUniqueItems = (array) => [...new Set(array)];// warning if you use it for array of strings

export const addCollapsedDataWorkflow = (res, t, timeZone) => res.map((result) => ({
  ...result,
  labelCollapsed: collapsedLabelsWorkflow(t)[result.statusCode] || '',
  actionDate: result?.actionDate ? getFullFormattedDate(result?.actionDate, timeZone, DAY_DATE_FORMAT) : '',
}));

export const sameArraysNoMatterOrder = (a, b) => a.length === b.length && a.every((el) => b.find((elc) => elc === el));

export const buildNewSelectedUser = (userSelected) => ({
  code: userSelected?.selected ? userSelected.userCode : '',
  department: userSelected?.selected ? userSelected.department : '',
  summary: userSelected?.selected ? formatSummaryUser(userSelected) : '',
});

export const parsePositiveOrZero = (value) => (value < 0 ? 0 : value);

export const parseObjectNegativeValuesToZero = (obj) => (obj
  ? Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, parsePositiveOrZero(value)]))
  : {});

const returnBlankIfNegative = (value) => value < 0 ? '' : value;

export const parseObjectNegativeValuesToBlank = (fieldsKeys = [], sourceObject) => {
  if (!sourceObject || !fieldsKeys.length) return {};

  return Object.fromEntries(fieldsKeys.map((field) => [field, returnBlankIfNegative(sourceObject?.[field])]));
};

export const removeIdFromObject = (object) => {
  const { id, ...restProps } = object;

  return restProps;
};

export const isString = (val) => typeof val === 'string' || val instanceof String;

export const removeParagraphTags = (text) => text?.replace(/<p>/g, '').replace(/<\/p>/g, '');

export const getLanguageTranslations = (languages) => (
  languages.reduce((translations, { code, name }) => ({ ...translations, [code]: name }), {})
);

export const hideFeedbackSubCR = (subCRInfo, extraStatus = []) => (
  [statusSubCampaign.OPEN, statusSubCampaign.IN_CHECKING, statusSubCampaign.IN_CONSOLIDATION, ...extraStatus].includes(subCRInfo?.status)
  && subCRInfo.workflowType === WORKFLOW_TYPE.DIGITAL
);

export const userSearchToUserInfo = (userSearch) => ({
  code: userSearch?.userCode || '',
  department: userSearch?.department || '',
  summary: userSearch?.userCode ? formatSummaryUser(userSearch) : '',
});

export const isValidYWPFortmat = (value) => /^\d{2}\/\d{2}\/\d{2}$/.test(value);

export const isValidPartNumber = (partNumber) => /^A\d{10}$/.test(partNumber);

export const isValidQeLevel = (qeLevel) => /^[QEqe]\d{3}$/.test(qeLevel);

export const isMbos = (mBosDetails) => mBosDetails?.isMbos === YES;

export const isFlashableRelease = (mBosDetails) => mBosDetails?.releaseFlashable === YES;

export const equalArraysEqual = (arr1, arr2) => {
  if (arr1.length !== arr2.length) {
    return false;
  }

  return arr1.every((element, index) => element === arr2[index]);
};

export const getAffectedModelSeries = (affectedPlants) => {
  const affectedModelSeries = [];
  affectedPlants?.forEach((plant) => {
    plant?.affectedSeries?.forEach((serie) => {
      if (!affectedModelSeries.includes(serie.modelSerie)) affectedModelSeries.push(serie.modelSerie);
    });
  });

  return affectedModelSeries.length > 0 ? affectedModelSeries.join(', ') : undefined;
};

export const getLocationInternalTranslation = (isLocation, lang) => {
  const isBoolean = (isLocation) => {
    return typeof isLocation === 'boolean';
  };

  if (isBoolean(isLocation)) {
    return INTERNAL_EXTERNAL?.[isLocation ? INTERNAL : EXTERNAL]?.[lang.toUpperCase()];
  }

  return '';
};

export const sortJsonKeysForComparison = (value) => {
  if (typeof value !== 'string') return value;

  try {
    const parsed = JSON.parse(value);

    return isObject(parsed) ? JSON.stringify(parsed, Object.keys(parsed).sort()) : value;
  } catch {
    return value;
  }
};
export const deepIsEqual = (dataA, dataB) => JSON.stringify(dataB) === JSON.stringify(dataA);
export const isPDRSCreatedCampaign = (campaignInfo) => (campaignInfo?.creationSystem === PDRS);
export const isFieldDisabledBYPRRD = (campaignInfo, fieldName = '') => (isPDRSCreatedCampaign(campaignInfo) && FIELDS_DISABLED_BY_PDRS.includes(fieldName));

export const getFieldCursorType = (isSelectionMode = false) => ({
  cursor: isSelectionMode ? 'pointer !important' : 'default',
});
export const getEditorId = (prefix, lang) => `${prefix}-${lang}`;

export const stopPropagation = (e) => {
  e?.stopPropagation();
  e?.nativeEvent?.stopImmediatePropagation?.();
};
export const isAssignInfoReady = (crInfo) => !!crInfo?.authorization && crInfo?.assignedUser !== undefined;

export const isPreviousToVanRemoval = (date) => {
  const VAN_FLOW_REMOVAL_DATE = new Date(2026, 5, 11); // 1st day with no VAN flow: Jun 11, 2026
  const formattedDate = stringToDate(date);

  if (!formattedDate) return false;

  return formattedDate < VAN_FLOW_REMOVAL_DATE;
};
export const isVans = (crType) => crType === CR_TYPE.VAN;
export const isRecipientsTab = (tab) => 'recipients' === tab;
export const isVansRecipientsTab = (crType, tab) => isVans(crType) && isRecipientsTab(tab);
export const isVansBeforeFlowRemoval = (crType, campaignDate) => isVans(crType) && isPreviousToVanRemoval(campaignDate);

export const toCamelCase = (errorCode) => {
  if (errorCode === null || errorCode === undefined) {
    return '';
  }

  const normalized = String(errorCode)
    .trim()
    .replace(/[_\-\s]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .toLowerCase();

  return normalized.replace(/\s+(.)/g, (_, char) => char.toUpperCase());
};
